import { Controller } from "@hotwired/stimulus"

// Animated "fan-out" diagram for the How It Works steps. On scroll-in the
// connector lines draw from the source and the targets pop in (once), then the
// finished diagram holds — it never dissolves, so the resting state is always
// complete. Subtle accents keep looping while in view: tagged elements pulse a
// green glow and the addendum chip pulses. Pauses offscreen and renders the
// finished static frame for reduced motion / no-JS.
//
// Markup is authored in ERB; this controller only animates elements tagged with
// data-flow: "line" (connectors), "node"/"check" (targets, with data-i for
// stagger), "pulse" (addendum) — plus any element with [data-glow] (looping glow).
const DRAW_MS = 620
const STAGGER = 260
const POP_MS = 380

export default class extends Controller {
  connect() {
    this.flowLines = Array.from(this.element.querySelectorAll('[data-flow="line"]'))
    this.fanTargets = Array.from(this.element.querySelectorAll('[data-flow="node"], [data-flow="check"]'))
    this.glows = Array.from(this.element.querySelectorAll("[data-glow]"))
    this.fades = Array.from(this.element.querySelectorAll("[data-fade]"))

    this.flowLines.forEach((line) => {
      // Straight <line> length via geometry — avoids getTotalLength(), which
      // throws on <line> in some browsers and would abort connect.
      const x1 = parseFloat(line.getAttribute("x1")) || 0
      const y1 = parseFloat(line.getAttribute("y1")) || 0
      const x2 = parseFloat(line.getAttribute("x2")) || 0
      const y2 = parseFloat(line.getAttribute("y2")) || 0
      line.dataset.length = Math.hypot(x2 - x1, y2 - y1)
      line.style.strokeDasharray = line.dataset.length
    })
    this.fanTargets.forEach((el) => {
      el.style.transformBox = "fill-box"
      el.style.transformOrigin = "center"
    })

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.flowLines.forEach((line) => (line.style.strokeDashoffset = line.dataset.length))
    this.fanTargets.forEach((target) => (target.style.opacity = "0"))

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting)
        if (visible) this.play()
        else this.pause()
      },
      { threshold: 0.25 }
    )
    this.observer.observe(this.element)
  }

  disconnect() {
    ;(this.animations || []).forEach((animation) => animation.cancel())
    if (this.observer) this.observer.disconnect()
  }

  play() {
    if (this.built) {
      this.animations.forEach((animation) => animation.play())
      return
    }
    this.built = true
    this.animations = []

    // Assemble once, then hold (fill: forwards keeps the finished frame).
    this.flowLines.forEach((line, i) => {
      this.animations.push(
        line.animate(
          [{ strokeDashoffset: Number(line.dataset.length) }, { strokeDashoffset: 0 }],
          { duration: DRAW_MS, delay: i * STAGGER, fill: "forwards", easing: "ease-out" }
        )
      )
    })
    this.fanTargets.forEach((target) => {
      const i = Number(target.dataset.i || 0)
      this.animations.push(
        target.animate(
          [
            { opacity: 0, transform: "scale(0.55)" },
            { opacity: 1, transform: "scale(1)" }
          ],
          {
            duration: POP_MS,
            delay: i * STAGGER + DRAW_MS * 0.7,
            fill: "forwards",
            easing: "cubic-bezier(0.34, 1.56, 0.64, 1)"
          }
        )
      )
    })

    // Subtle perpetual accents, starting once the diagram has assembled.
    const loopDelay = this.flowLines.length * STAGGER + DRAW_MS

    this.glows.forEach((el, i) => {
      this.animations.push(
        el.animate(
          [
            { filter: "drop-shadow(0px 0px 0px rgba(34, 197, 94, 0))" },
            { filter: "drop-shadow(0px 0px 7px rgba(34, 197, 94, 0.95))" },
            { filter: "drop-shadow(0px 0px 0px rgba(34, 197, 94, 0))" }
          ],
          { duration: 1000, iterations: Infinity, delay: loopDelay + i * 220, easing: "ease-in-out" }
        )
      )
    })
    this.fades.forEach((el, i) => {
      this.animations.push(
        el.animate([{ opacity: 1 }, { opacity: 0.3 }, { opacity: 1 }], {
          duration: 1500,
          iterations: Infinity,
          delay: loopDelay + i * 180,
          easing: "ease-in-out"
        })
      )
    })
  }

  pause() {
    ;(this.animations || []).forEach((animation) => animation.pause())
  }
}
